//#region IMPORT
import React, { useEffect, useState } from 'react';
import M_TbmEntry_BASIC from './M_TbmEntry_BASIC';
import { useDispatch, useSelector } from 'react-redux';
import useFileUpload from '../../../../Hooks/useFileUpload';
import { CommonActions } from '../../../../Reducer/CommonReducer';
// useBackStack 임포트
import { useBackStack } from '../../../../Hooks/back/useBackStack';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { useTbmSessionSave } from '../hook/useSaveTbmSession';
import dayjs from 'dayjs';
//#endregion

const MAX_PHOTOS = 1;
const ETC_LABEL = '기타';

function M_TbmEntry_BASICF(props) {
  //#region 변수
  const dispatch = useDispatch();
  const { sessionData, stepData } = useSelector((state) => state.tbm);
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const activeStep = useSelector((state) => state.common.activeStep);

  // 제목: TBM(날짜) 형식으로 자동 생성, 사용자 입력 없음
  const [tbmTitle] = useState(() => `TBM(${dayjs().format('YYYY-MM-DD')})`);

  const [inputs, setInputs] = useState({
    workLocations: [], // 선택된 작업 장소(중복 선택)
    etcLocationText: '', // 작업 장소 '기타' 수기 입력
    locationContents: {}, // 장소별 작업 내용 { [장소]: 내용 }
    hazards: [], // 선택된 잠재위험요인(중복 선택)
    etcHazardText: '', // 잠재위험요인 '기타' 수기 입력
    countermeasure: '', // 잠재위험요인 대책(단일 작성칸)
    photoNm: '',
    photoImg: null,
    photoSrc: '',
  });

  const tbmUpload = useFileUpload({
    oldFilePathField: 'entryPath',
    newFileNameField: 'saveName',
    getTargetPath: () => `TBM/${sessionData.sessionCd}/Photo`,
    maxCount: MAX_PHOTOS,
  });
  //#endregion

  //#region 커스텀훅
  // 회의록 작성 단계 진입 후에는 이전 단계로 돌아갈 수 없음 (참석자 서명 진행 중)
  useBackStack({
    type: 'snackbar',
    onBack: () =>
      dispatch(
        showSnackbar({
          open: true,
          message: '회의록 작성 중에는 이전 화면으로 돌아갈 수 없습니다.',
          type: 'warning',
        })
      ),
  });
  const { SaveTbmSession } = useTbmSessionSave({
    fileSave: async () => {
      const finalPath = await tbmUpload.SaveLogicFile();
      return { entryPath: finalPath };
    },
  });
  //#endregion

  //#region 이펙트
  const Init = async () => {
    try {
      const currentStep = stepData.find((f) => f.stepCode === props.stepCode);
      SaveTbmSession([
        {
          ...sessionData,
          curStepOrder: currentStep?.stepOrder,
          curStepCode: currentStep?.stepCode,
          curPosition: currentStep?.lastPosition,
          curContentOrder: currentStep?.curContentOrder ?? 1,
        },
      ]);
    } catch (e) {
      console.log(e);
      dispatch(
        showSnackbar({
          open: true,
          message: e.message,
          type: 'error',
        })
      );
    }
  };
  useEffect(() => {
    Init();
    return () => {
      tbmUpload.clearFiles();
    };
  }, []);
  useEffect(() => {
    const list = (tbmUpload.previewList ?? []).filter((img) => !img.deleted);
    setInputs((prev) => {
      if (list.length === 0) {
        return { ...prev, photoNm: '', photoSrc: '', photoImg: null };
      }
      const first = list[0];
      return {
        ...prev,
        photoNm: first.fileName || '',
        photoSrc: first.preview || '',
        photoImg: null,
      };
    });
  }, [tbmUpload.previewList]);
  //#endregion

  //#region 선택 토글
  const ToggleWorkLocation = (loc) => {
    setInputs((prev) => {
      const selected = prev.workLocations.includes(loc);
      const workLocations = selected
        ? prev.workLocations.filter((v) => v !== loc)
        : [...prev.workLocations, loc];
      const locationContents = { ...prev.locationContents };
      if (selected) {
        delete locationContents[loc];
      } else if (locationContents[loc] === undefined) {
        locationContents[loc] = '';
      }
      return {
        ...prev,
        workLocations,
        locationContents,
        etcLocationText: selected && loc === ETC_LABEL ? '' : prev.etcLocationText,
      };
    });
  };

  const SetLocationContent = (loc, value) => {
    setInputs((prev) => ({
      ...prev,
      locationContents: { ...prev.locationContents, [loc]: value },
    }));
  };

  const ToggleHazard = (hz) => {
    setInputs((prev) => {
      const selected = prev.hazards.includes(hz);
      const hazards = selected
        ? prev.hazards.filter((v) => v !== hz)
        : [...prev.hazards, hz];
      return {
        ...prev,
        hazards,
        etcHazardText: selected && hz === ETC_LABEL ? '' : prev.etcHazardText,
      };
    });
  };
  //#endregion

  //#region 유효성 검사
  const LocationLabel = (loc) =>
    loc === ETC_LABEL ? `기타(${inputs.etcLocationText.trim()})` : loc;
  const HazardLabel = (hz) =>
    hz === ETC_LABEL ? `기타(${inputs.etcHazardText.trim()})` : hz;

  const hasLocation = inputs.workLocations.length > 0;
  const etcLocationValid =
    !inputs.workLocations.includes(ETC_LABEL) || !!inputs.etcLocationText.trim();
  const allLocationContentsFilled = inputs.workLocations.every((loc) =>
    (inputs.locationContents[loc] || '').trim()
  );
  const hasHazard = inputs.hazards.length > 0;
  const etcHazardValid =
    !inputs.hazards.includes(ETC_LABEL) || !!inputs.etcHazardText.trim();
  const hasCountermeasure = !!inputs.countermeasure.trim();

  const isValid =
    hasLocation &&
    etcLocationValid &&
    allLocationContentsFilled &&
    hasHazard &&
    etcHazardValid &&
    hasCountermeasure;
  //#endregion

  //#region 저장
  const SaveLogic1 = async () => {
    if (!isValid) {
      dispatch(
        showSnackbar({
          message:
            '작업 장소·내용, 잠재위험요인, 대책을 모두 입력해주세요.',
          type: 'warning',
        })
      );
      return;
    }

    const end = dayjs();
    const endDt = end.format('YYYY-MM-DD HH:mm:ss');
    const startMs = dayjs(
      String(sessionData.startDt).replace(' ', 'T')
    ).valueOf();
    const totalElapsedTime = Math.max(
      0,
      Math.floor((end.valueOf() - startMs) / 1000)
    );

    const tbmAddress = inputs.workLocations.map(LocationLabel).join(', ');
    const tbmContent = JSON.stringify({
      locationContents: inputs.workLocations.reduce((acc, loc) => {
        acc[LocationLabel(loc)] = inputs.locationContents[loc] || '';
        return acc;
      }, {}),
      hazards: inputs.hazards.map(HazardLabel),
      countermeasure: inputs.countermeasure.trim(),
    });

    await SaveTbmSession([
      {
        ...sessionData,
        sessionStatus: 'CLOSE',
        tbmDate: dayjs().format('YYYY-MM-DD'),
        tbmTitle,
        tbmContent,
        tbmAddress,
        endDt,
        totalElapsedTime,
      },
    ]);

    dispatch(CommonActions.setActiveStep(activeStep + 1));
    props.ActiveButton({
      type: 'stepChange',
      step: activeStep + 1,
    });
    props.ActiveButton({
      type: 'end',
      content: true,
      waiting: 'N',
    });

    tbmUpload.clearFiles();
  };
  //#endregion

  return (
    <M_TbmEntry_BASIC
      MAX_PHOTOS={MAX_PHOTOS}
      tbmTitle={tbmTitle}
      inputs={inputs}
      setInputs={setInputs}
      ToggleWorkLocation={ToggleWorkLocation}
      SetLocationContent={SetLocationContent}
      ToggleHazard={ToggleHazard}
      isValid={isValid}
      tbmUpload={tbmUpload}
      SaveLogic1={SaveLogic1}
      ActiveButton={props.ActiveButton}
      userInfo={userInfo}
    />
  );
}

export default M_TbmEntry_BASICF;
